import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  ShieldCheck,
  Sparkles,
  ClipboardList,
  Activity,
  Stethoscope,
  Droplets,
  ArrowRight,
  Lock,
  FileCheck2,
} from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  LinkButton,
  FeatureCard,
  Card,
  Eyebrow,
  Stat,
  Badge,
} from "../components/ui";
import { useSeo } from "../lib/seo";
import { SITE_URL, APP_URL } from "../lib/env";

const SCHEMA_ORG = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "WeConsult Rx",
  url: SITE_URL,
  logo: `${SITE_URL}/logo-icon.svg`,
  sameAs: [],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "hello@weconsultrx.com",
      areaServed: "US",
    },
  ],
  description:
    "Modern consultant pharmacy operations platform — clinical engine, compliance automation, and AI-augmented workflow for SNF, ASC, and ESRD dialysis.",
};

function useSchemaOrg() {
  useEffect(() => {
    const id = "ld-org";
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(SCHEMA_ORG);
    return () => {
      el?.remove();
    };
  }, []);
}

export function Home() {
  useSeo({
    title: "WeConsult Rx — Modern consultant pharmacy operations",
    description:
      "Replace fragmented chart review, paper recommendations, and manual bundling with one HIPAA-grade platform built for ASCP-aligned SNF, AAAHC ASC, and CMS ESRD dialysis consultant pharmacists.",
    path: "/",
  });
  useSchemaOrg();

  return (
    <>
      {/* ============ Hero ============ */}
      <section className="relative overflow-hidden bg-canvas pt-16 pb-24 md:pt-24 md:pb-32">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-60"
          style={{
            background:
              "radial-gradient(circle at 20% 0%, rgba(21,128,61,0.10), transparent 40%), radial-gradient(circle at 90% 30%, rgba(20,83,45,0.08), transparent 45%)",
          }}
        />
        <Container>
          <div className="max-w-3xl">
            <Badge>HIPAA-grade · forest from day one</Badge>
            <Heading level={1} className="mt-5">
              Modern consultant pharmacy operations,{" "}
              <span className="text-forest-700">evidence-driven AI</span>.
            </Heading>
            <Lede className="mt-6">
              Replace fragmented chart review, paper recommendations, and manual
              bundling with one platform built for ASCP-aligned SNF, AAAHC ASC,
              and CMS ESRD dialysis review.
            </Lede>
            <div className="mt-8 flex flex-wrap gap-3">
              <LinkButton to="/demo" size="lg">
                Request a demo
                <ArrowRight className="h-4 w-4" aria-hidden />
              </LinkButton>
              <LinkButton href={APP_URL} variant="secondary" size="lg">
                Sign in
              </LinkButton>
            </div>
            <p className="mt-4 text-sm text-ink-500">
              No credit card. White-glove onboarding. Sample reviews in 14 days.
            </p>
          </div>
        </Container>
      </section>

      {/* ============ Social proof strip ============ */}
      <section className="bg-surface border-y border-border py-10">
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <Stat value="49" label="Clinical rules" />
            <Stat value="3" label="Rule packs" />
            <Stat value="1857" label="Automated tests" />
            <Stat value="68" label="PHI gates" />
          </div>
          <p className="mt-6 text-center text-xs uppercase tracking-tagline text-ink-500">
            Codified ASCP &middot; AAAHC ch 11 &middot; CMS ESRD CfC &middot; AAMI
          </p>
        </Container>
      </section>

      {/* ============ Three pillars ============ */}
      <Section>
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>The platform</Eyebrow>
            <Heading level={2} className="mt-2">
              Three pillars. One workflow.
            </Heading>
            <Lede className="mt-4 text-ink-600">
              Built for consultant pharmacists who need both clinical depth and
              operational throughput — without the spreadsheets.
            </Lede>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <FeatureCard
              icon={<Activity className="h-6 w-6" aria-hidden />}
              title="Clinical engine"
            >
              3 rule packs &middot; 49 rules across ACB (Boustani 2008), STOPP/START
              v2, Beers 2023, CDC core elements, and AAMI water quality. Evidence
              cited per rule.
            </FeatureCard>
            <FeatureCard
              icon={<ShieldCheck className="h-6 w-6" aria-hidden />}
              title="Compliance automation"
            >
              CMS F-tags, state board filings, Adobe Sign workflows, append-only
              audit log, and HIPAA gates locked in code &mdash; not policy
              documents.
            </FeatureCard>
            <FeatureCard
              icon={<Sparkles className="h-6 w-6" aria-hidden />}
              title="AI-augmented workflow"
            >
              Claude Sonnet 4.6 via AWS Bedrock with per-tenant prompt learning,
              allergy-suppression resistance, and cost cap controls.
            </FeatureCard>
          </div>
        </Container>
      </Section>

      {/* ============ Use cases ============ */}
      <Section tone="soft">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>For your facility</Eyebrow>
            <Heading level={2} className="mt-2">
              Built for the verticals that need it most.
            </Heading>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <UseCaseCard
              icon={<ClipboardList className="h-6 w-6" aria-hidden />}
              title="Long-term care (SNF)"
              body="ASCP standards · F757 / F758 / F881 · Boustani ACB · GDR psychotropic"
              to="/solutions/snf"
            />
            <UseCaseCard
              icon={<Stethoscope className="h-6 w-6" aria-hidden />}
              title="Ambulatory surgical (ASC)"
              body="AAAHC handbook v41 ch 11 · DEA · high-alert med monitoring · vaccine storage"
              to="/solutions/asc"
            />
            <UseCaseCard
              icon={<Droplets className="h-6 w-6" aria-hidden />}
              title="Dialysis (ESRD)"
              body="42 CFR 494 · AAMI water · ESA Hgb · MBD labs · CFR §494.50 hemodialyzer reuse"
              to="/solutions/dialysis"
            />
          </div>
        </Container>
      </Section>

      {/* ============ Trust band ============ */}
      <Section>
        <Container>
          <Card className="md:p-12">
            <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] md:items-center">
              <div>
                <Eyebrow>Trust &amp; compliance</Eyebrow>
                <Heading level={2} className="mt-2">
                  Compliance isn’t a checklist. It’s shipped in code.
                </Heading>
                <p className="mt-4 text-ink-600">
                  68 PHI safety gates enforced at the test layer. RLS pen-test
                  suite on every commit. BAAs with every covered subprocessor.
                  Append-only audit log. Tenant-scoped Supabase Vault for every
                  secret.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <LinkButton to="/trust" variant="primary" size="md">
                    Visit the trust center
                  </LinkButton>
                  <LinkButton to="/hipaa" variant="secondary" size="md">
                    HIPAA controls
                  </LinkButton>
                </div>
              </div>
              <ul className="grid gap-3">
                <TrustItem icon={<Lock className="h-5 w-5" aria-hidden />} label="AES-256 at rest · TLS 1.2+ in transit" />
                <TrustItem icon={<ShieldCheck className="h-5 w-5" aria-hidden />} label="HIPAA §164.308 / .310 / .312 controls" />
                <TrustItem icon={<FileCheck2 className="h-5 w-5" aria-hidden />} label="BAA on every covered subprocessor" />
                <TrustItem icon={<Activity className="h-5 w-5" aria-hidden />} label="Append-only audit log · 7-year retention" />
              </ul>
            </div>
          </Card>
        </Container>
      </Section>

      {/* ============ Final CTA ============ */}
      <Section tone="forest" className="text-center">
        <Container>
          <Heading level={2} className="text-white">
            See WeConsult Rx in your workflow.
          </Heading>
          <p className="mt-4 text-white/85 max-w-2xl mx-auto">
            Bring a sample chart. We’ll show you a flagged review and bundle
            inside 30 minutes &mdash; with citations.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <LinkButton to="/demo" size="lg" className="bg-white text-forest-900 hover:bg-ink-100">
              Request a demo
            </LinkButton>
            <LinkButton to="/contact" variant="ghost" size="lg" className="text-white border border-white/30 hover:bg-white/10">
              Talk to us
            </LinkButton>
          </div>
        </Container>
      </Section>
    </>
  );
}

function UseCaseCard({
  icon,
  title,
  body,
  to,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  to: string;
}) {
  return (
    <Card as="article" className="flex flex-col h-full">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-forest-700 text-white">
        {icon}
      </div>
      <Heading level={3} className="mt-5">
        {title}
      </Heading>
      <p className="mt-3 text-ink-600">{body}</p>
      <div className="mt-6">
        <Link
          to={to}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:text-forest-900"
        >
          Explore the vertical
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </Card>
  );
}

function TrustItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <li className="flex items-start gap-3 rounded-xl border border-border bg-canvas p-4">
      <span className="text-forest-700 mt-0.5">{icon}</span>
      <span className="text-sm text-ink-800 font-medium">{label}</span>
    </li>
  );
}
